import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
// import { useTranslation } from "react-i18next";
import { getMenuUrl, useRestaurantId } from "../utils/restaurant";
import {
  Container,
  Title,
  Paper,
  Stack,
  Text,
  Button,
  Center,
  Loader,
  Alert,
  Group,
  Badge,
  Divider,
} from "@mantine/core";
import {
  IconCheck,
  IconAlertCircle,
  IconCopy,
  IconArrowLeft,
} from "@tabler/icons-react";
import { apiClient } from "../lib/apiClient";
import { formatPrice } from "../utils/currency";
import { notifications } from "@mantine/notifications";
import { useMenuItems } from "../queries/menuQueries";

interface Order {
  id: string;
  total_amount: number;
  pickup_code: string | null;
  pickup_code_expires_at: string | null;
  picked_up: boolean;
  status: string;
  items: Array<{
    id: string;
    quantity: number;
    unit_price: number;
    subtotal: number;
    menu_item_id: string;
  }>;
  created_on: string;
}

export default function SuccessPage() {
  // const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  // Get restaurant ID (La Vida)
  const restaurantId = useRestaurantId();

  // Fetch menu items for item name lookup
  const { data: menuItems } = useMenuItems(true, restaurantId);

  // Create a map for quick menu item lookup
  const menuById = useMemo(() => {
    const map: Record<string, { name: string }> = {};
    (menuItems || []).forEach((m) => {
      map[m.id] = { name: m.name };
    });
    return map;
  }, [menuItems]);

  const parseServerDate = (iso: string) => {
    // Treat server datetimes without timezone as UTC
    // Append 'Z' if no timezone info is present
    const hasTimezone =
      iso.endsWith("Z") ||
      /[+-]\d{2}:\d{2}$/.test(iso) ||
      /[+-]\d{4}$/.test(iso);
    const normalized = hasTimezone ? iso : `${iso}Z`;
    return new Date(normalized);
  };

  useEffect(() => {
    if (!sessionId) {
      setError("Missing session ID");
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      try {
        const data = await apiClient<Order>(`/orders/by-session/${sessionId}`);
        setOrder(data);
        setError(null);

        // Save order to localStorage for order history
        try {
          const stored = localStorage.getItem("orderHistory");
          let orders: Order[] = stored ? JSON.parse(stored) : [];

          // Check if order already exists
          const exists = orders.some((o) => o.id === data.id);
          if (!exists) {
            // Add new order
            orders.push({
              id: data.id,
              total_amount: data.total_amount,
              status: data.status,
              created_on: data.created_on,
              items: data.items.map((item) => ({
                id: item.id,
                quantity: item.quantity,
                menu_item_id: item.menu_item_id,
                unit_price: item.unit_price,
                subtotal: item.subtotal,
              })),
              pickup_code: data.pickup_code,
              pickup_code_expires_at: data.pickup_code_expires_at,
              picked_up: data.picked_up,
            });

            // Limit to last 10 orders
            if (orders.length > 10) {
              orders = orders
                .sort(
                  (a, b) =>
                    new Date(b.created_on).getTime() -
                    new Date(a.created_on).getTime()
                )
                .slice(0, 10);
            }

            localStorage.setItem("orderHistory", JSON.stringify(orders));
          }
        } catch (storageErr) {
          // Silently fail if localStorage is unavailable
          console.error("Failed to save order to localStorage:", storageErr);
        }
      } catch (err) {
        console.error("Failed to fetch order:", err);
        setError("Failed to load order details");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [sessionId]);

  // Countdown timer
  useEffect(() => {
    if (!order?.pickup_code_expires_at) return;

    const updateTimer = () => {
      const expiresAt = parseServerDate(
        order.pickup_code_expires_at!
      ).getTime();
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setTimeRemaining(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [order?.pickup_code_expires_at]);

  const handleCopyCode = () => {
    if (order?.pickup_code) {
      navigator.clipboard.writeText(order.pickup_code);
      notifications.show({
        title: "Kode Kopieret",
        message: "Afhentningskode kopieret til udklipsholderen",
        color: "teal",
      });
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !order) {
    return (
      <Container py="md">
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          title="Error"
          mb="md"
        >
          {error || "Order not found"}
        </Alert>
        <Button
          component={Link}
          to={getMenuUrl(restaurantId)}
          leftSection={<IconArrowLeft size={16} />}
        >
          Back to Menu
        </Button>
      </Container>
    );
  }

  const isExpired =
    order.pickup_code_expires_at &&
    parseServerDate(order.pickup_code_expires_at) < new Date();
  const isPickedUp = order.picked_up;
  const showCode = order.pickup_code && !isExpired && !isPickedUp;

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        {showCode ? (
          <>
            <Paper withBorder p="xl" style={{ textAlign: "center" }}>
              <Stack gap="md" align="center">
                <IconCheck size={64} color="green" stroke={2} />
                <Title order={1} c="green">
                  Ordrer Bekræftet
                </Title>
                <Text size="lg" c="dimmed">
                  Din ordre er blevet bekræftet
                </Text>
              </Stack>
            </Paper>

            <Paper withBorder p="xl" style={{ textAlign: "center" }}>
              <Stack gap="md" align="center">
                <Text size="sm" c="dimmed" fw={500}>
                  Din afhentningskode
                </Text>
                <Title
                  order={1}
                  size={48}
                  fw={700}
                  data-testid="pickup-code"
                  style={{
                    fontFamily: "monospace",
                    letterSpacing: "0.2em",
                    color: "var(--mantine-color-brand-6)",
                    wordBreak: "break-all",
                    fontSize: "clamp(2rem, 10vw, 3rem)",
                  }}
                >
                  {order.pickup_code}
                </Title>
                <Button
                  data-testid="copy-pickup-code"
                  leftSection={<IconCopy size={16} />}
                  onClick={handleCopyCode}
                  variant="light"
                >
                  Kopier kode
                </Button>
                {timeRemaining !== null && timeRemaining > 0 && (
                  <Badge color="orange" size="lg">
                    Expires in {formatTime(timeRemaining)}
                  </Badge>
                )}
                <Text size="sm" c="dimmed" mt="md">
                  Vis denne kode ved afhentning. Kode udløber om 30 minutter.
                </Text>
              </Stack>
            </Paper>
          </>
        ) : (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="orange"
            title="Ordre Status"
          >
            {isPickedUp
              ? "Denne ordre er allerede afhentet."
              : isExpired
              ? "Afhentningskoden for denne ordre er udløbet. Kontakt restauranter for at få hjælp."
              : "Kan ikke vise afhentningskode."}
          </Alert>
        )}

        <Paper withBorder p="md">
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={600}>Ordre Total</Text>
              <Text fw={700} size="lg">
                {formatPrice(order.total_amount)}
              </Text>
            </Group>
            <Divider />
            <Group justify="space-between">
              <Text c="dimmed">Order ID</Text>
              <Text
                size="sm"
                style={{ fontFamily: "monospace" }}
                data-testid="order-id"
              >
                {order.id.slice(0, 8)}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text c="dimmed">Ordre Tid</Text>
              <Text size="sm">
                {new Date(order.created_on).toLocaleString()}
              </Text>
            </Group>
            <Group justify="space-between" align="flex-start">
              <Text c="dimmed">Varer</Text>
              <Stack gap={4} align="flex-end">
                {order.items.map((item) => (
                  <Text key={item.id} size="sm">
                    {item.quantity}×{" "}
                    {menuById[item.menu_item_id]?.name ||
                      `Vare #${item.menu_item_id.slice(0, 8)}`}
                  </Text>
                ))}
              </Stack>
            </Group>
          </Stack>
        </Paper>

        <Button
          component={Link}
          to={getMenuUrl(restaurantId)}
          fullWidth
          size="lg"
          variant="light"
          leftSection={<IconArrowLeft size={16} />}
        >
          Tilbage til Menu
        </Button>
      </Stack>
    </Container>
  );
}
